import { useEffect, useMemo, useRef, useState } from "react";
import { Image, Typography } from "antd";

interface DayQuote {
  text?: string;
  author?: string;
}

interface QuoteConfig {
  quotes?: DayQuote[];
  sender_name?: string;
  sender_avatar?: string;
  category?: string;
  background?: string;
  video?: string;
  list_image?: string[];
}

interface Props {
  config: QuoteConfig;
}

/* ─── Constants ─── */
const DAY_LABELS = ["Thứ Hai", "Thứ Ba", "Thứ Tư", "Thứ Năm", "Thứ Sáu", "Thứ Bảy", "Chủ Nhật"];
const DAY_SHORT = ["T2", "T3", "T4", "T5", "T6", "T7", "CN"];
const DAY_EMOJIS = ["🌱", "🔥", "💧", "⚡", "🌟", "🎯", "☀️"];
const DAY_MOODS = [
  { accent: "#2d6a4f", bg: "rgba(45,106,79,0.06)", label: "Khởi đầu mới" },
  { accent: "#e76f51", bg: "rgba(231,111,81,0.06)", label: "Năng lượng" },
  { accent: "#457b9d", bg: "rgba(69,123,157,0.06)", label: "Bình yên" },
  { accent: "#9b5de5", bg: "rgba(155,93,229,0.06)", label: "Sáng tạo" },
  { accent: "#f4a261", bg: "rgba(244,162,97,0.06)", label: "Tự do" },
  { accent: "#e63946", bg: "rgba(230,57,70,0.06)", label: "Đam mê" },
  { accent: "#fb8500", bg: "rgba(251,133,0,0.06)", label: "Nghỉ ngơi" },
];

/* ─── Ink Particles ─── */
const InkParticles = ({ color }: { color: string }) => {
  const [dots] = useState(() =>
    Array.from({ length: 10 }, (_, i) => ({
      id: i,
      left: 10 + Math.random() * 80,
      top: Math.random() * 100,
      delay: Math.random() * 10,
      duration: 14 + Math.random() * 10,
      size: 2 + Math.random() * 3,
      opacity: 0.03 + Math.random() * 0.05,
    })),
  );

  return (
    <>
      {dots.map((d) => (
        <div
          key={d.id}
          className="fixed pointer-events-none"
          style={{
            left: `${d.left}%`,
            top: `${d.top}%`,
            width: `${d.size}px`,
            height: `${d.size}px`,
            borderRadius: "50%",
            background: color,
            opacity: d.opacity,
            animation: `qd-drift ${d.duration}s ease-in-out ${d.delay}s infinite`,
            zIndex: 1,
          }}
        />
      ))}
    </>
  );
};

/* ─── Animated Section ─── */
const AnimatedSection = ({
  children,
  className = "",
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setTimeout(() => setVisible(true), delay);
      },
      { threshold: 0.15 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [delay]);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        transition: "opacity 0.9s ease, transform 0.9s ease",
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(30px)",
      }}
    >
      {children}
    </div>
  );
};

/* ═══════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════ */
const PublicQuoteOfTheDay = ({ config }: Props) => {
  // Get today's day index (0=Mon, 6=Sun)
  const todayIndex = useMemo(() => {
    const jsDay = new Date().getDay(); // 0=Sun
    return jsDay === 0 ? 6 : jsDay - 1;
  }, []);

  const [activeDay, setActiveDay] = useState(todayIndex);
  const [animKey, setAnimKey] = useState(0);

  const mood = DAY_MOODS[activeDay];
  const currentQuote = config.quotes?.[activeDay];
  const isToday = activeDay === todayIndex;

  const handleDayChange = (index: number) => {
    if (index === activeDay) return;
    setActiveDay(index);
    setAnimKey((k) => k + 1);
  };

  return (
    <div className="min-h-screen relative overflow-x-hidden" style={{ background: "#faf8f4" }}>
      {/* Global Styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Newsreader:ital,opsz,wght@0,6..72,300;0,6..72,400;0,6..72,700;0,6..72,800;1,6..72,300;1,6..72,400;1,6..72,700&family=Outfit:wght@200;300;400;500&family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,700;0,9..144,900;1,9..144,300;1,9..144,700&display=swap');

        @keyframes qd-drift {
          0%, 100% { transform: translate(0, 0); }
          33% { transform: translate(8px, -12px); }
          66% { transform: translate(-6px, 8px); }
        }
        @keyframes qd-line-draw {
          0% { transform: scaleX(0); }
          100% { transform: scaleX(1); }
        }
        @keyframes qd-quote-in {
          0% { opacity: 0; transform: translateY(25px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes qd-fade-in {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes qd-scroll-dot {
          0% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(8px); }
        }
        @keyframes qd-pulse-ring {
          0%, 100% { transform: scale(1); opacity: 0.3; }
          50% { transform: scale(1.15); opacity: 0.6; }
        }
        @keyframes qd-quote-pulse {
          0%, 100% { opacity: 0.05; }
          50% { opacity: 0.09; }
        }

        .qd-serif { font-family: 'Newsreader', Georgia, serif; }
        .qd-display { font-family: 'Fraunces', Georgia, serif; }
        .qd-sans { font-family: 'Outfit', sans-serif; }

        .qd-card {
          background: #fff;
          border: 1px solid rgba(26,26,26,0.06);
          box-shadow: 0 1px 3px rgba(26,26,26,0.03), 0 8px 30px rgba(26,26,26,0.04);
        }

        .qd-hero-overlay {
          background: linear-gradient(180deg,
            rgba(250,248,244,0) 0%,
            rgba(250,248,244,0.5) 50%,
            rgba(250,248,244,0.95) 80%,
            #faf8f4 100%
          );
        }

        .qd-day-btn {
          transition: all 0.3s ease;
          cursor: pointer;
          user-select: none;
        }
        .qd-day-btn:hover {
          transform: translateY(-2px);
        }

        .qd-gallery-img {
          transition: transform 0.5s cubic-bezier(0.25,0.46,0.45,0.94), box-shadow 0.5s ease;
        }
        .qd-gallery-img:hover {
          transform: scale(1.02) translateY(-4px);
          box-shadow: 0 20px 50px rgba(26,26,26,0.1);
        }

        .qd-grain::after {
          content: '';
          position: fixed;
          inset: 0;
          opacity: 0.018;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
          pointer-events: none;
          z-index: 0;
        }
      `}</style>

      <div className="qd-grain" />
      <InkParticles color={mood.accent} />

      {/* ══════════ TOP BAR ══════════ */}
      <div className="sticky top-0 z-40" style={{ background: "rgba(250,248,244,0.9)", backdropFilter: "blur(12px)" }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3">
          <span
            className="qd-sans"
            style={{
              fontSize: "0.6rem",
              letterSpacing: "0.25em",
              color: "rgba(26,26,26,0.3)",
              textTransform: "uppercase",
            }}
          >
            Quote of the Day
          </span>
          {config.category && (
            <span
              className="qd-sans px-3 py-1 rounded-full"
              style={{
                fontSize: "0.55rem",
                letterSpacing: "0.12em",
                color: mood.accent,
                textTransform: "uppercase",
                border: `1px solid ${mood.accent}30`,
                background: mood.bg,
              }}
            >
              {config.category}
            </span>
          )}
        </div>

        {/* ── Day Navigation ── */}
        <div className="flex items-center justify-center gap-1 px-4 pb-3">
          {DAY_SHORT.map((label, i) => {
            const isActive = i === activeDay;
            const isTodayDot = i === todayIndex;
            return (
              <div
                key={i}
                className="qd-day-btn flex flex-col items-center gap-1"
                onClick={() => handleDayChange(i)}
                style={{ flex: 1, maxWidth: 48 }}
              >
                <span
                  className="qd-sans"
                  style={{
                    fontSize: "0.55rem",
                    letterSpacing: "0.05em",
                    color: isActive ? mood.accent : "rgba(26,26,26,0.3)",
                    fontWeight: isActive ? 500 : 300,
                  }}
                >
                  {label}
                </span>
                <div
                  className="flex items-center justify-center"
                  style={{
                    width: isActive ? 36 : 30,
                    height: isActive ? 36 : 30,
                    borderRadius: "50%",
                    background: isActive ? mood.accent : "transparent",
                    border: isActive
                      ? "none"
                      : `1.5px solid ${isTodayDot ? mood.accent + "50" : "rgba(26,26,26,0.08)"}`,
                    transition: "all 0.3s ease",
                    position: "relative",
                  }}
                >
                  <span style={{ fontSize: isActive ? "0.85rem" : "0.75rem", transition: "all 0.3s ease" }}>
                    {DAY_EMOJIS[i]}
                  </span>
                  {/* Today indicator dot */}
                  {isTodayDot && !isActive && (
                    <div
                      className="absolute"
                      style={{
                        bottom: -3,
                        width: 4,
                        height: 4,
                        borderRadius: "50%",
                        background: mood.accent,
                        animation: "qd-pulse-ring 2s ease-in-out infinite",
                      }}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Thin line */}
        <div style={{ height: 1, background: "rgba(26,26,26,0.05)" }} />
      </div>

      {/* ══════════ HERO — THE QUOTE ══════════ */}
      <section
        className="relative flex flex-col items-center justify-center overflow-hidden"
        style={{ minHeight: "75vh" }}
      >
        {/* Background image (optional, subtle) */}
        {config.background && (
          <div className="absolute inset-0">
            <img
              src={config.background}
              alt=""
              className="w-full h-full object-cover"
              style={{ opacity: 0.1, filter: "grayscale(60%)" }}
            />
            <div className="qd-hero-overlay absolute inset-0" />
          </div>
        )}

        {/* Quote Content — animated on day change */}
        <div key={animKey} className="relative z-10 px-6 py-16 max-w-lg mx-auto w-full">
          {/* Day label */}
          <div className="flex items-center gap-2 mb-8" style={{ animation: "qd-fade-in 0.6s ease forwards" }}>
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: mood.accent,
              }}
            />
            <span
              className="qd-sans"
              style={{
                fontSize: "0.7rem",
                letterSpacing: "0.15em",
                color: mood.accent,
                textTransform: "uppercase",
                fontWeight: 500,
              }}
            >
              {DAY_LABELS[activeDay]} {isToday && "• Hôm nay"}
            </span>
            <span className="qd-sans" style={{ fontSize: "0.65rem", color: "rgba(26,26,26,0.2)", marginLeft: "auto" }}>
              {mood.label}
            </span>
          </div>

          {/* Opening quote mark */}
          <span
            className="qd-serif"
            style={{
              fontSize: "clamp(5rem, 18vw, 7rem)",
              lineHeight: 0.7,
              color: mood.accent,
              opacity: 0.07,
              display: "block",
              userSelect: "none",
              animation: "qd-quote-pulse 4s ease-in-out infinite",
            }}
          >
            "
          </span>

          {/* The Quote */}
          {currentQuote?.text ? (
            <blockquote
              className="qd-display"
              style={{
                fontSize: "clamp(1.5rem, 5vw, 2.2rem)",
                fontWeight: 700,
                lineHeight: 1.45,
                color: "#1a1a1a",
                margin: "-16px 0 0 0",
                padding: "0 4px",
                letterSpacing: "-0.01em",
                animation: "qd-quote-in 0.7s ease forwards",
              }}
            >
              {currentQuote.text}
            </blockquote>
          ) : (
            <p
              className="qd-serif"
              style={{
                fontSize: "1.1rem",
                color: "rgba(26,26,26,0.25)",
                fontStyle: "italic",
                animation: "qd-quote-in 0.7s ease forwards",
              }}
            >
              Chưa có câu trích dẫn cho {DAY_LABELS[activeDay]}...
            </p>
          )}

          {/* Closing quote mark */}
          <div className="text-right" style={{ marginTop: -8 }}>
            <span
              className="qd-serif"
              style={{
                fontSize: "clamp(5rem, 18vw, 7rem)",
                lineHeight: 0.7,
                color: mood.accent,
                opacity: 0.07,
                userSelect: "none",
                animation: "qd-quote-pulse 4s ease-in-out 1s infinite",
              }}
            >
              "
            </span>
          </div>

          {/* Accent line */}
          <div
            style={{
              width: 48,
              height: 3,
              borderRadius: 2,
              background: mood.accent,
              transformOrigin: "left",
              animation: "qd-line-draw 0.8s ease-out forwards",
              marginTop: 16,
              marginBottom: 20,
            }}
          />

          {/* Author */}
          {currentQuote?.author && (
            <div style={{ animation: "qd-quote-in 0.8s ease 0.15s both" }}>
              <p
                className="qd-serif"
                style={{
                  fontSize: "1rem",
                  fontWeight: 700,
                  color: "#1a1a1a",
                  margin: 0,
                }}
              >
                — {currentQuote.author}
              </p>
            </div>
          )}

          {/* Scroll indicator */}
          <div className="mt-16 flex flex-col items-center gap-2">
            <span
              className="qd-sans"
              style={{
                fontSize: "0.6rem",
                letterSpacing: "0.25em",
                color: "rgba(26,26,26,0.18)",
                textTransform: "uppercase",
              }}
            >
              Xem thêm
            </span>
            <div
              className="flex items-start justify-center"
              style={{
                width: 18,
                height: 28,
                borderRadius: 9,
                border: `1.5px solid ${mood.accent}30`,
                padding: 3,
              }}
            >
              <div
                style={{
                  width: 4,
                  height: 4,
                  borderRadius: "50%",
                  background: mood.accent,
                  opacity: 0.6,
                  animation: "qd-scroll-dot 1.5s ease-in-out infinite",
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ══════════ ALL 7 QUOTES OVERVIEW ══════════ */}
      <section className="relative py-14 px-4">
        {/* Section divider */}
        <div className="flex items-center justify-center gap-4 mb-10">
          <div
            className="h-px flex-1 max-w-16"
            style={{ background: "linear-gradient(to right, transparent, rgba(26,26,26,0.1))" }}
          />
          <span className="qd-serif" style={{ fontSize: "1rem", color: "rgba(26,26,26,0.12)", fontStyle: "italic" }}>
            §
          </span>
          <div
            className="h-px flex-1 max-w-16"
            style={{ background: "linear-gradient(to left, transparent, rgba(26,26,26,0.1))" }}
          />
        </div>

        <AnimatedSection className="text-center mb-8">
          <span
            className="qd-sans"
            style={{
              fontSize: "0.6rem",
              letterSpacing: "0.25em",
              color: "rgba(26,26,26,0.28)",
              textTransform: "uppercase",
            }}
          >
            Cảm hứng cả tuần
          </span>
          <Typography.Title
            level={3}
            className="qd-display"
            style={{ color: "#1a1a1a", marginTop: 4, fontWeight: 700 }}
          >
            7 Ngày — 7 Câu Nói
          </Typography.Title>
          <div className="mx-auto mt-2" style={{ width: 32, height: 2, background: "#1a1a1a", borderRadius: 1 }} />
        </AnimatedSection>

        {/* Quote cards list */}
        <div className="max-w-md mx-auto flex flex-col gap-3">
          {DAY_LABELS.map((day, i) => {
            const q = config.quotes?.[i];
            const m = DAY_MOODS[i];
            const isActiveCard = i === activeDay;

            return (
              <AnimatedSection key={i} delay={i * 80}>
                <div
                  className="qd-day-btn rounded-2xl relative overflow-hidden"
                  onClick={() => {
                    handleDayChange(i);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  style={{
                    padding: "16px 20px",
                    background: isActiveCard ? m.bg : "#fff",
                    border: `1px solid ${isActiveCard ? m.accent + "25" : "rgba(26,26,26,0.05)"}`,
                    boxShadow: isActiveCard ? `0 4px 20px ${m.accent}10` : "0 1px 3px rgba(26,26,26,0.02)",
                  }}
                >
                  {/* Active indicator */}
                  {isActiveCard && (
                    <div
                      className="absolute left-0 top-0 bottom-0"
                      style={{ width: 3, background: m.accent, borderRadius: "0 2px 2px 0" }}
                    />
                  )}

                  <div className="flex items-start gap-3">
                    {/* Day circle */}
                    <div
                      className="flex-shrink-0 flex items-center justify-center"
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        background: isActiveCard ? m.accent : "rgba(26,26,26,0.03)",
                        transition: "all 0.3s ease",
                      }}
                    >
                      <span style={{ fontSize: "1rem" }}>{DAY_EMOJIS[i]}</span>
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span
                          className="qd-sans"
                          style={{
                            fontSize: "0.7rem",
                            fontWeight: 500,
                            color: isActiveCard ? m.accent : "rgba(26,26,26,0.5)",
                            letterSpacing: "0.05em",
                          }}
                        >
                          {day}
                        </span>
                        {i === todayIndex && (
                          <span
                            className="qd-sans px-2 py-0.5 rounded-full"
                            style={{
                              fontSize: "0.5rem",
                              letterSpacing: "0.1em",
                              color: m.accent,
                              background: m.accent + "15",
                              textTransform: "uppercase",
                            }}
                          >
                            Hôm nay
                          </span>
                        )}
                      </div>

                      {q?.text ? (
                        <p
                          className="qd-serif"
                          style={{
                            fontSize: "0.88rem",
                            color: "rgba(26,26,26,0.7)",
                            lineHeight: 1.5,
                            margin: 0,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          "{q.text}"
                        </p>
                      ) : (
                        <p
                          className="qd-sans"
                          style={{ fontSize: "0.8rem", color: "rgba(26,26,26,0.2)", margin: 0, fontStyle: "italic" }}
                        >
                          Chưa có quote...
                        </p>
                      )}

                      {q?.author && (
                        <p
                          className="qd-sans"
                          style={{ fontSize: "0.7rem", color: "rgba(26,26,26,0.35)", margin: 0, marginTop: 4 }}
                        >
                          — {q.author}
                        </p>
                      )}
                    </div>

                    {/* Arrow */}
                    <span
                      className="flex-shrink-0"
                      style={{
                        color: isActiveCard ? m.accent : "rgba(26,26,26,0.15)",
                        fontSize: "0.8rem",
                        marginTop: 4,
                      }}
                    >
                      →
                    </span>
                  </div>
                </div>
              </AnimatedSection>
            );
          })}
        </div>
      </section>

      {/* ══════════ SENDER ══════════ */}
      {(config.sender_name || config.sender_avatar) && (
        <section className="relative py-10 px-4">
          <AnimatedSection className="flex flex-col items-center text-center">
            {config.sender_avatar && (
              <Image
                src={config.sender_avatar}
                alt="sender"
                width={56}
                height={56}
                preview={false}
                className="rounded-full object-cover mb-3"
                style={{
                  border: `2px solid ${mood.accent}20`,
                  filter: "grayscale(15%)",
                }}
              />
            )}
            {config.sender_name && (
              <>
                <span
                  className="qd-sans"
                  style={{
                    fontSize: "0.6rem",
                    letterSpacing: "0.2em",
                    color: "rgba(26,26,26,0.3)",
                    textTransform: "uppercase",
                  }}
                >
                  Chia sẻ bởi
                </span>
                <p
                  className="qd-serif"
                  style={{ fontSize: "1.1rem", fontWeight: 700, color: "#1a1a1a", margin: "4px 0 0 0" }}
                >
                  {config.sender_name}
                </p>
              </>
            )}
          </AnimatedSection>
        </section>
      )}

      {/* ══════════ VIDEO ══════════ */}
      {config.video && (
        <section className="relative py-10 px-4">
          <AnimatedSection>
            <div className="text-center mb-5">
              <span
                className="qd-sans"
                style={{
                  fontSize: "0.6rem",
                  letterSpacing: "0.25em",
                  color: "rgba(26,26,26,0.28)",
                  textTransform: "uppercase",
                }}
              >
                Xem thêm
              </span>
              <Typography.Title level={4} className="qd-serif" style={{ color: "#1a1a1a", marginTop: 4 }}>
                Video
              </Typography.Title>
            </div>
            <div className="qd-card rounded-2xl overflow-hidden max-w-md mx-auto" style={{ padding: 6 }}>
              <video
                src={config.video}
                controls
                playsInline
                className="w-full rounded-xl"
                style={{ aspectRatio: "16/9", objectFit: "cover" }}
                poster={config.background}
              />
            </div>
          </AnimatedSection>
        </section>
      )}

      {/* ══════════ GALLERY ══════════ */}
      {config.list_image && config.list_image.length > 0 && (
        <section className="relative py-12 px-4">
          <div className="flex items-center justify-center gap-4 mb-8">
            <div
              className="h-px flex-1 max-w-16"
              style={{ background: "linear-gradient(to right, transparent, rgba(26,26,26,0.1))" }}
            />
            <span className="qd-serif" style={{ fontSize: "1rem", color: "rgba(26,26,26,0.1)", fontStyle: "italic" }}>
              ❧
            </span>
            <div
              className="h-px flex-1 max-w-16"
              style={{ background: "linear-gradient(to left, transparent, rgba(26,26,26,0.1))" }}
            />
          </div>

          <AnimatedSection className="text-center mb-8">
            <span
              className="qd-sans"
              style={{
                fontSize: "0.6rem",
                letterSpacing: "0.25em",
                color: "rgba(26,26,26,0.28)",
                textTransform: "uppercase",
              }}
            >
              Hình ảnh
            </span>
            <Typography.Title
              level={3}
              className="qd-display"
              style={{ color: "#1a1a1a", marginTop: 4, fontWeight: 700 }}
            >
              Minh Hoạ
            </Typography.Title>
            <div className="mx-auto mt-2" style={{ width: 32, height: 2, background: mood.accent, borderRadius: 1 }} />
          </AnimatedSection>

          <div className="max-w-md mx-auto flex flex-col gap-4">
            {config.list_image.map((img, i) => (
              <AnimatedSection key={i} delay={i * 100}>
                <div
                  className="qd-gallery-img rounded-2xl overflow-hidden relative"
                  style={{ border: "1px solid rgba(26,26,26,0.06)" }}
                >
                  <Image
                    src={img}
                    alt={`gallery-${i}`}
                    className="w-full object-cover"
                    style={{
                      aspectRatio: i % 3 === 0 ? "16/10" : "3/2",
                      display: "block",
                      filter: "contrast(1.02) saturate(0.9)",
                    }}
                  />
                  <div
                    className="absolute bottom-3 right-3 flex items-center justify-center"
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      background: "rgba(255,255,255,0.85)",
                      backdropFilter: "blur(8px)",
                      border: "1px solid rgba(26,26,26,0.08)",
                    }}
                  >
                    <span
                      className="qd-serif"
                      style={{ fontSize: "0.65rem", fontWeight: 700, color: "rgba(26,26,26,0.5)" }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </section>
      )}

      {/* ══════════ CLOSING ══════════ */}
      <section className="relative py-20 px-4">
        <AnimatedSection className="text-center">
          <div className="mb-4" style={{ opacity: 0.05 }}>
            <span className="qd-display" style={{ fontSize: "5rem", lineHeight: 0.6 }}>
              ❝
            </span>
          </div>

          <h3
            className="qd-display mb-4"
            style={{
              fontSize: "clamp(1.4rem, 5vw, 1.9rem)",
              fontWeight: 900,
              color: "#1a1a1a",
              letterSpacing: "-0.02em",
            }}
          >
            Mỗi Ngày Một Cảm Hứng
          </h3>

          <p
            className="qd-serif max-w-xs mx-auto mb-8"
            style={{ fontSize: "0.9rem", color: "rgba(26,26,26,0.3)", fontStyle: "italic", lineHeight: 1.8 }}
          >
            7 ngày, 7 câu nói, 7 nguồn năng lượng cho tuần mới của bạn.
          </p>

          <div className="flex flex-col items-center gap-1">
            <div style={{ width: 1, height: 24, background: "rgba(26,26,26,0.08)" }} />
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                border: `1.5px solid ${mood.accent}30`,
              }}
            />
            <div style={{ width: 1, height: 24, background: "rgba(26,26,26,0.08)" }} />
          </div>
        </AnimatedSection>
      </section>

      {/* ══════════ FOOTER ══════════ */}
      <footer className="relative py-8 px-6" style={{ borderTop: "1px solid rgba(26,26,26,0.05)" }}>
        <div className="flex items-center justify-between max-w-md mx-auto">
          <Typography.Text
            className="qd-sans"
            style={{ color: "rgba(26,26,26,0.18)", fontSize: "0.6rem", letterSpacing: "0.1em" }}
          >
            QUOTE OF THE DAY
          </Typography.Text>
          <Typography.Text
            className="qd-sans"
            style={{ color: "rgba(26,26,26,0.18)", fontSize: "0.6rem", letterSpacing: "0.05em" }}
          >
            Powered by Miraway
          </Typography.Text>
        </div>
      </footer>
    </div>
  );
};

export default PublicQuoteOfTheDay;
